import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Chamada = ({
  API,
  getAuthHeaders,
  students,
  loadAttendanceForTurma,
  selectedTurma,
  setSelectedTurma,
  selectedDate,
  setSelectedDate,
  turmas,
  getFilteredTurmas,
  isSunday,
  loadDashboard,
  currentUser,
  setLoading,
  loading,
  setCurrentView
}) => {
  const [turmaAtendance, setTurmaAtendance] = useState([]);
  const [turmaDataGlobal, setTurmaDataGlobal] = useState({
    ofertas_total: '',
    biblias_total: '',
    revistas_total: '',
    visitantes_total: '',
    pos_chamada_total: ''
  });

  // Estados para revista e lição atual
  const [revistaAtual, setRevistaAtual] = useState(null);
  const [licaoAtual, setLicaoAtual] = useState(null);

  useEffect(() => {
    if (selectedTurma) {
      loadTurmaData();
      loadRevistaData(); // Carregar dados da revista
    }
  }, [selectedTurma, selectedDate]);

  // Nova função para carregar dados da revista baseada na data
  const loadRevistaData = async () => {
    if (!selectedTurma) {
      // Limpar estados quando não há turma selecionada
      setRevistaAtual(null);
      setLicaoAtual(null);
      return;
    }

    try {
      // Buscar revista da turma passando a data selecionada
      const url = `${API}/revistas/turma/${selectedTurma}${selectedDate ? `?data=${selectedDate}` : ''}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });
      const revista = response.data;

      if (revista && revista.tema) {
        // Buscar lição do dia baseada na data selecionada (exata)
        if (revista.licoes && revista.licoes.length > 0) {
          const licaoHoje = revista.licoes.find(licao => licao.data === selectedDate);

          if (licaoHoje) {
            // Se encontrou lição para a data, mostrar revista e lição
            setRevistaAtual(revista);
            setLicaoAtual(licaoHoje);
          } else {
            // Se não encontrou lição para a data, não mostrar nada
            setRevistaAtual(null);
            setLicaoAtual(null);
          }
        } else {
          setRevistaAtual(null);
          setLicaoAtual(null);
        }
      } else {
        setRevistaAtual(null);
        setLicaoAtual(null);
      }
    } catch (error) {
      setRevistaAtual(null);
      setLicaoAtual(null);
    }
  };

  const loadTurmaData = async () => {
    if (!selectedTurma) return;

    try {
      const studentsInTurma = students.filter(s => s.turma_id === selectedTurma);

      const existingAttendance = await loadAttendanceForTurma(selectedTurma, selectedDate);

      // Calcular totais existentes
      let totalOfertas = 0;
      let totalBiblias = 0;
      let totalRevistas = 0;
      let totalVisitantes = 0;
      let totalPosChamada = 0;

      const attendanceMap = {};
      existingAttendance.forEach(att => {
        attendanceMap[att.aluno_id] = att;
        // Usar parseFloat com toFixed para evitar acúmulo de imprecisões
        totalOfertas = parseFloat((totalOfertas + (att.oferta || 0)).toFixed(2));
        totalBiblias += att.biblias_entregues || 0;
        totalRevistas += att.revistas_entregues || 0;
        if (att.status === 'visitante') totalVisitantes++;
        if (att.status === 'pos_chamada') totalPosChamada++;
      });

      // Garantir precisão dos valores decimais e formato brasileiro (vírgula)
      setTurmaDataGlobal({
        ofertas_total: totalOfertas > 0 ? totalOfertas.toFixed(2).replace('.', ',') : '',
        biblias_total: totalBiblias > 0 ? totalBiblias.toString() : '',
        revistas_total: totalRevistas > 0 ? totalRevistas.toString() : '',
        visitantes_total: totalVisitantes > 0 ? totalVisitantes.toString() : '',
        pos_chamada_total: totalPosChamada > 0 ? totalPosChamada.toString() : ''
      });

      const attendanceData = studentsInTurma.map(student => ({
        aluno_id: student.id,
        nome: student.nome_completo,
        presente: attendanceMap[student.id]?.status === 'presente' || false
      }));

      setTurmaAtendance(attendanceData);
    } catch (error) {
    }
  };

  const togglePresenca = (alunoId) => {
    setTurmaAtendance(prev =>
      prev.map(att =>
        att.aluno_id === alunoId ? { ...att, presente: !att.presente } : att
      )
    );
  };

  const updateTurmaData = (field, value) => {
    // Se for campo de oferta, aceitar APENAS vírgula (padrão brasileiro)
    if (field === 'ofertas_total') {
      // Permitir apenas números e vírgula, até 2 casas decimais
      const regex = /^\d*,?\d{0,2}$/;
      if (value === '' || regex.test(value)) {
        setTurmaDataGlobal(prev => ({
          ...prev,
          [field]: value  // Manter exatamente como digitado
        }));
      }
    } else {
      setTurmaDataGlobal(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const presentesCount = turmaAtendance.filter(att => att.presente).length;

      // Converter valores para números, tratando strings vazias como 0
      // Converter vírgula para ponto antes do parseFloat
      const ofertaTotal = parseFloat((turmaDataGlobal.ofertas_total || '0').replace(',', '.')) || 0;
      const bibliasTotal = parseInt(turmaDataGlobal.biblias_total) || 0;
      const revistasTotal = parseInt(turmaDataGlobal.revistas_total) || 0;
      const visitantesTotal = parseInt(turmaDataGlobal.visitantes_total) || 0;
      const posChamadaTotal = parseInt(turmaDataGlobal.pos_chamada_total) || 0;

      const attendanceList = turmaAtendance.map((att, index) => {
        let status = att.presente ? 'presente' : 'ausente';
        let oferta = 0;
        let biblias = 0;
        let revistas = 0;

        // Encontrar índice do primeiro presente
        const firstPresentIndex = turmaAtendance.findIndex(a => a.presente);

        // NÃO dividir ofertas automaticamente - valor total vai apenas para o primeiro presente
        if (att.presente && index === firstPresentIndex) {
          oferta = ofertaTotal; // Valor total apenas no primeiro presente
        }

        // Dar bíblias e revistas apenas para o primeiro aluno (para não duplicar)
        if (index === 0) {
          biblias = bibliasTotal;
          revistas = revistasTotal;
        }

        // CORREÇÃO: Aplicar visitantes e pós-chamada como registros adicionais
        // (sem sobrescrever seleções manuais do usuário)

        return {
          aluno_id: att.aluno_id,
          status: status,
          oferta: oferta,
          biblias_entregues: biblias,
          revistas_entregues: revistas
        };
      });

      // ADICIONAR registros de visitantes e pós-chamada como entradas separadas
      // (igual ao sistema de bíblias/revistas - sem afetar alunos já selecionados)

      // Adicionar visitantes (criar IDs únicos para registros temporários)
      for (let i = 0; i < visitantesTotal; i++) {
        attendanceList.push({
          aluno_id: `visitante_${Date.now()}_${i}`, // ID único para visitante
          status: 'visitante',
          oferta: 0,
          biblias_entregues: 0,
          revistas_entregues: 0
        });
      }

      // Adicionar pós-chamada (criar IDs únicos para registros temporários)
      for (let i = 0; i < posChamadaTotal; i++) {
        attendanceList.push({
          aluno_id: `pos_chamada_${Date.now()}_${i}`, // ID único para pós-chamada
          status: 'pos_chamada',
          oferta: 0,
          biblias_entregues: 0,
          revistas_entregues: 0
        });
      }

      await axios.post(`${API}/attendance/bulk/${selectedTurma}?data=${selectedDate}&user_tipo=${currentUser.tipo}&user_id=${currentUser.user_id || currentUser.id}`, attendanceList, { headers: getAuthHeaders() });


      await loadDashboard();
      alert('Chamada salva com sucesso!');
    } catch (error) {
      if (error.response?.data?.detail) {
        alert(`Erro: ${error.response.data.detail}`);
      } else {
        alert('Erro ao salvar chamada');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            ← Voltar ao Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Fazer Chamada</h1>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Turma</label>
              <select
                value={selectedTurma || ''}
                onChange={(e) => setSelectedTurma(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Selecione uma turma</option>
                {getFilteredTurmas().map(turma => (
                  <option key={turma.id} value={turma.id}>{turma.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Data</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {!isSunday(selectedDate) && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              <strong>Aviso:</strong> A data selecionada não é um domingo.
            </div>
          )}

          {selectedTurma && (
            <div>
              {/* Campos Globais da Turma */}
              <div className="bg-blue-50 p-4 md:p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold text-blue-800 mb-4">Dados da Turma</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">Oferta Total (R$)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={turmaDataGlobal.ofertas_total || ''}
                      onChange={(e) => updateTurmaData('ofertas_total', e.target.value)}
                      className="w-full px-3 py-3 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0,00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">Bíblias</label>
                    <input
                      type="text"
                      value={turmaDataGlobal.biblias_total || ''}
                      onChange={(e) => updateTurmaData('biblias_total', e.target.value)}
                      className="w-full px-3 py-3 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">Revistas</label>
                    <input
                      type="text"
                      value={turmaDataGlobal.revistas_total || ''}
                      onChange={(e) => updateTurmaData('revistas_total', e.target.value)}
                      className="w-full px-3 py-3 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">Visitantes</label>
                    <input
                      type="text"
                      value={turmaDataGlobal.visitantes_total || ''}
                      onChange={(e) => updateTurmaData('visitantes_total', e.target.value)}
                      className="w-full px-3 py-3 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">Pós-Chamada</label>
                    <input
                      type="text"
                      value={turmaDataGlobal.pos_chamada_total || ''}
                      onChange={(e) => updateTurmaData('pos_chamada_total', e.target.value)}
                      className="w-full px-3 py-3 text-lg border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

          {/* Informações da Revista e Lição Atual - NOVA SEÇÃO */}
          {selectedTurma && revistaAtual && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 md:p-6 mb-6 border-l-4 border-blue-500">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Tema da Revista */}
                <div>
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">📚</span>
                    <h3 className="text-lg font-semibold text-blue-800">Revista Trimestral</h3>
                  </div>
                  <p className="text-sm text-blue-700 leading-relaxed">
                    {revistaAtual.tema}
                  </p>
                </div>

                {/* Lição do Dia */}
                <div>
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">🎯</span>
                    <h3 className="text-lg font-semibold text-green-800">
                      {licaoAtual ? 'Lição de Hoje' : 'Lição da Data'}
                    </h3>
                  </div>
                  {licaoAtual ? (
                    <div>
                      <p className="text-sm font-medium text-green-700 mb-1">
                        {licaoAtual.titulo}
                      </p>
                      <p className="text-xs text-green-600">
                        📅 {new Date(licaoAtual.data + 'T00:00:00').toLocaleDateString('pt-BR', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <p>Nenhuma lição programada para esta data</p>
                      {revistaAtual.licoes && revistaAtual.licoes.length > 0 && (
                        <p className="text-xs mt-1">
                          Próxima lição: {revistaAtual.licoes[0].titulo} ({revistaAtual.licoes[0].data})
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Indicador visual de disponibilidade */}
              <div className="mt-4 pt-3 border-t border-blue-200">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-blue-600">
                    📖 Total de lições: {revistaAtual.licoes ? revistaAtual.licoes.length : 0}
                  </span>
                  {licaoAtual && (
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                      ✨ Lição Disponível
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Aviso se não há revista configurada */}
          {selectedTurma && !revistaAtual && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-lg">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    <strong>📚 Atenção:</strong> Não há revista configurada para esta turma.
                    Entre em contato com o administrador para configurar o material didático.
                  </p>
                </div>
              </div>
            </div>
          )}

              {/* Lista de Presença Simples */}
              <div className="bg-white border rounded-lg overflow-hidden mb-6">
                <div className="bg-gray-100 px-4 py-4 border-b">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-800">
                      {turmas.find(t => t.id === selectedTurma)?.nome}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Clique nos nomes para marcar presença</p>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm">
                      <span className="text-green-700 font-medium">
                        Presentes: {turmaAtendance.filter(att => att.presente).length}
                      </span>
                      <span className="text-red-700 font-medium">
                        Ausentes: {turmaAtendance.filter(att => !att.presente).length}
                      </span>
                      <span className="text-blue-700 font-medium">
                        Frequência: {turmaAtendance.length > 0
                          ? ((turmaAtendance.filter(att => att.presente).length / turmaAtendance.length) * 100).toFixed(1).replace('.', ',')
                          : '0,0'}%
                      </span>
                    </div>
                  </div>

                  {/* Botão Salvar - Agora responsivo */}
                  <div className="w-full">
                    <button
                      onClick={handleSave}
                      disabled={loading}
                      className="w-full px-6 py-4 bg-green-500 text-white rounded-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-400 text-lg font-semibold shadow-lg"
                    >
                      {loading ? 'Salvando...' : '💾 Salvar Chamada'}
                    </button>
                  </div>
                </div>

                <div className="p-4 md:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {turmaAtendance.map((att, index) => (
                      <button
                        key={index}
                        onClick={() => togglePresenca(att.aluno_id)}
                        className={`p-3 md:p-4 rounded-lg text-left transition-all duration-200 touch-manipulation ${
                          att.presente
                            ? 'bg-green-100 border-2 border-green-500 text-green-800'
                            : 'bg-gray-100 border-2 border-gray-300 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-4 h-4 rounded-full mr-3 flex-shrink-0 ${
                            att.presente ? 'bg-green-500' : 'bg-gray-400'
                          }`}></div>
                          <span className="font-medium text-sm md:text-base break-words">{att.nome}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Resumo */}
              <div className="p-4 md:p-6 bg-green-50 rounded-lg">
                <h4 className="text-lg font-semibold text-green-800 mb-3">Resumo</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-green-600">
                      {turmaAtendance.filter(att => att.presente).length}
                    </div>
                    <div className="text-xs md:text-sm text-green-700">Presentes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-red-600">
                      {turmaAtendance.filter(att => !att.presente).length}
                    </div>
                    <div className="text-xs md:text-sm text-red-700">Ausentes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-blue-600">
                      {turmaAtendance.length}
                    </div>
                    <div className="text-xs md:text-sm text-blue-700">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-purple-600">
                      {turmaAtendance.length > 0
                        ? ((turmaAtendance.filter(att => att.presente).length / turmaAtendance.length) * 100).toFixed(1).replace('.', ',')
                        : '0,0'}%
                    </div>
                    <div className="text-xs md:text-sm text-purple-700">Frequência</div>
                  </div>
                </div>
              </div>
            </div>
          )}


        </div>
      </div>
    </div>
  );
};

export default Chamada;
